import { GridApi } from "ag-grid-community";
import { AddressType } from "~/graphql/mainEnums";
import {
  type GetProfileInfoQuery,
  type ComplianceIndividualProfileInput,
  type ComplianceInstitutionProfileInput,
} from "~/graphql/supergraphTypes";
import { findUsStateMatch } from "~/pages/Compliance/documents/CaseCreationPreview/stateMatcher";
import { ConflictRowData } from "./gridConfig";

export type ConflictProfileData = NonNullable<GetProfileInfoQuery["complianceProfile"]>;

export const getSelectedValuesFromGrid = (
  gridApi: GridApi<ConflictRowData>,
): Record<string, string | null> => {
  const selectedValues: Record<string, string | null> = {};

  gridApi.forEachNode((node) => {
    const rowData = node.data;
    if (!rowData) return;

    const { fieldName, currentValue, extractedValue, selectedSource } = rowData;

    // If extracted is selected, use extractedValue; otherwise default to currentValue
    const value = selectedSource === "extracted" ? extractedValue : currentValue;
    selectedValues[fieldName] = value === "--" ? null : value;
  });

  return selectedValues;
};

/**
 * Builds the address input from selected values and current profile data.
 * For fields not present in selectedValues (i.e. non-conflicting fields
 * filtered out of the grid), the current profile values are preserved.
 */
const buildAddressInput = (
  selectedValues: Record<string, string | null>,
  currentProfile: ConflictProfileData,
) => {
  const primaryAddress = currentProfile.addresses?.find((addr) => addr.isPrimaryDomicile);

  let street: string | null = primaryAddress?.street ?? null;
  let street2: string | null = primaryAddress?.street2 ?? null;
  let street3: string | null = primaryAddress?.street3 ?? null;

  if ("Street address" in selectedValues) {
    const streetAddressValue = selectedValues["Street address"];
    if (streetAddressValue) {
      const parts = streetAddressValue.split(",").map((part) => part.trim());
      street = parts[0] || null;
      street2 = parts[1] || null;
      street3 = parts[2] || null;
    } else {
      street = null;
      street2 = null;
      street3 = null;
    }
  }

  return {
    addressType: AddressType.Unknown,
    street,
    street2,
    street3,
    city: "City" in selectedValues ? selectedValues["City"] : (primaryAddress?.city ?? null),
    state:
      "State" in selectedValues
        ? findUsStateMatch(selectedValues["State"]) || selectedValues["State"]
        : (primaryAddress?.state?.name ?? primaryAddress?.province ?? null),
    postalCode:
      "Zip code" in selectedValues
        ? selectedValues["Zip code"]
        : (primaryAddress?.postalCode ?? null),
    country: primaryAddress?.country?.name,
    subprovince: primaryAddress?.subprovince,
  };
};

/**
 * Builds the individual profile input for the mutation
 */
export const buildIndividualProfileInput = (
  selectedValues: Record<string, string | null>,
  currentProfile: ConflictProfileData,
  caseId: number,
): ComplianceIndividualProfileInput => {
  const addressInput = buildAddressInput(selectedValues, currentProfile);

  const dateOfBirth =
    "Date of birth" in selectedValues
      ? selectedValues["Date of birth"] || ""
      : currentProfile.dateOfBirth || "";

  return {
    firstName: currentProfile.firstName || "",
    middleName: currentProfile.middleName || null,
    lastName: currentProfile.lastName || "",
    dateOfBirth,
    addresses: [addressInput],
    caseId: caseId,
  };
};

/**
 * Builds the institution profile input for the mutation
 */
export const buildInstitutionProfileInput = (
  selectedValues: Record<string, string | null>,
  currentProfile: ConflictProfileData,
  caseId: number,
): ComplianceInstitutionProfileInput => {
  const addressInput = buildAddressInput(selectedValues, currentProfile);

  const dateOfFormation =
    "Date of organization" in selectedValues
      ? selectedValues["Date of organization"] || null
      : currentProfile.dateOfOrganization || null;

  return {
    legalBusinessName: currentProfile.legalName || "",
    dateOfFormation,
    addresses: [addressInput],
    caseId: caseId,
  };
};
